import { ICoreContext } from '@msdyn365-commerce/core';
import { AsyncResultStatus, Customer } from '@msdyn365-commerce/retail-proxy';
import { useEffect } from 'react';

import {
    getNewsletterConsentEligibility,
    NEWSLETTER_ATTRIBUTE_NAME,
    NEWSLETTER_CHOICE_CLAIM,
    synchronizeNewsletterConsent
} from '../../../shared/services/newsletter-consent-service';
import { _readFromSessionStorage, _writeToSessionStorage } from '../../../utils/helper';

export interface IUseNewsletterConsentSyncOptions {
    enabled?: boolean;
    context: ICoreContext;
    customer?: Customer;
    customerStatus: AsyncResultStatus;
}

const NEWSLETTER_RETRY_KEY_PREFIX = 'bs_newsletter_retry_count';
const NEWSLETTER_MAX_RETRIES = 3;
const NEWSLETTER_BASE_DELAY_MS = 5000;
const NEWSLETTER_COMPLETED_MARKER = '-1';

const getRetryKey = (accountNumber: string): string => `${NEWSLETTER_RETRY_KEY_PREFIX}:${accountNumber}`;

const isSyncCompleted = (accountNumber: string): boolean =>
    _readFromSessionStorage(getRetryKey(accountNumber)) === NEWSLETTER_COMPLETED_MARKER;

const readRetryCount = (accountNumber: string): number => {
    const storedValue = Number.parseInt(_readFromSessionStorage(getRetryKey(accountNumber)) ?? '0', 10);
    return Number.isNaN(storedValue) ? 0 : storedValue;
};

/**
 * One-way newsletter consent synchronization: when the signed-in token carries
 * the affirmative newsletter choice claim and the D365 attribute reports 0,
 * the customer is updated to subscribed. Retries up to three times per account
 * per browser session with increasing delays (5s, 15s, 45s).
 */
export const useNewsletterConsentSync = (options: IUseNewsletterConsentSyncOptions): void => {
    const { enabled = false, context, customer, customerStatus } = options;

    const isAuthenticated = context.request.user?.isAuthenticated ?? false;

    // Re-runs when the customer refreshes (e.g. after our own update); the session marker prevents duplicate syncs.
    useEffect(() => {
        if (typeof window === 'undefined' || !enabled || !isAuthenticated || customerStatus !== 'SUCCESS') {
            return;
        }

        const accountNumber = customer?.AccountNumber;
        if (!customer || !accountNumber || isSyncCompleted(accountNumber)) {
            return;
        }

        const eligibility = getNewsletterConsentEligibility({
            customer,
            token: context.request.user.token,
            attributeName: NEWSLETTER_ATTRIBUTE_NAME,
            choiceClaim: NEWSLETTER_CHOICE_CLAIM
        });
        if (eligibility !== 'eligible') {
            return;
        }

        let isCancelled = false;
        let timerId: number | undefined;

        const attemptSynchronization = (): void => {
            synchronizeNewsletterConsent({
                context,
                customer,
                token: context.request.user.token,
                attributeName: NEWSLETTER_ATTRIBUTE_NAME,
                choiceClaim: NEWSLETTER_CHOICE_CLAIM
            })
                .then(() => {
                    _writeToSessionStorage(getRetryKey(accountNumber), NEWSLETTER_COMPLETED_MARKER);
                })
                .catch((error: Error) => {
                    if (isCancelled) {
                        return;
                    }
                    const retryCount = readRetryCount(accountNumber);
                    if (retryCount >= NEWSLETTER_MAX_RETRIES) {
                        context.telemetry.error(`[Newsletter] FAILED all - max retries exceeded - Account: ${accountNumber}`);
                        return;
                    }
                    _writeToSessionStorage(getRetryKey(accountNumber), (retryCount + 1).toString());
                    context.telemetry.error(
                        `[Newsletter] FAILED one - Account: ${accountNumber} - retrying (${retryCount + 1}) - error ${error.message}`
                    );
                    timerId = window.setTimeout(attemptSynchronization, NEWSLETTER_BASE_DELAY_MS * Math.pow(3, retryCount));
                });
        };

        attemptSynchronization();

        return () => {
            isCancelled = true;
            if (timerId !== undefined) {
                window.clearTimeout(timerId);
            }
        };
    }, [enabled, isAuthenticated, customerStatus, customer, context]);
};
